import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';

export const useCalendarPreferences = () => {
  const { isAuthenticated, token, refreshToken } = useAuth();
  const [calendarData, setCalendarData] = useState({
    calendars: [],
    loading: true,
    error: null,
    lastFetch: null
  });

  const fetchCalendars = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setCalendarData({
        calendars: [],
        loading: false,
        error: null,
        lastFetch: null
      });
      return [];
    }

    try {
      setCalendarData(prev => ({ ...prev, loading: true, error: null }));

      // First, try to refresh the token to ensure it's valid
      const freshToken = await refreshToken();
      const tokenToUse = freshToken || token;

      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenToUse}`
      };

      let response = await fetch('/api/google/calendars/list', { method: 'GET', headers });

      let data = await response.json();

      if (!response.ok) {
        console.log('Calendar API response not OK:', { status: response.status, data });
        
        // If token expired, retry once with a fresh token
        if (response.status === 401) {
          const retryToken = await refreshToken();
          if (retryToken) {
            const retryHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${retryToken}` };
            response = await fetch('/api/google/calendars/list', { method: 'GET', headers: retryHeaders });
            data = await response.json();
            if (response.ok) {
              setCalendarData({
                calendars: data.calendars || [],
                loading: false,
                error: null,
                lastFetch: new Date()
              });
              return data.calendars;
            }
          }
        }

        // Handle specific API error responses
        if (response.status === 401 && (data.requiresAuth || (data.error && data.error.includes('not connected')))) {
          // This is expected when Google Calendar isn't connected yet
          setCalendarData({
            calendars: [],
            loading: false,
            error: 'google_calendar_not_connected',
            lastFetch: new Date()
          });
          return [];
        }
        
        // Handle any other API errors gracefully
        setCalendarData({
          calendars: [],
          loading: false,
          error: 'google_calendar_not_connected',
          lastFetch: new Date()
        });
        return [];
      }

      setCalendarData({
        calendars: data.calendars || [],
        loading: false,
        error: null,
        lastFetch: new Date()
      });

      return data.calendars;
    } catch (error) {
      console.error('Error fetching calendars (handled gracefully):', error);
      
      // Always handle errors gracefully - never throw to prevent runtime crashes
      setCalendarData({
        calendars: [],
        loading: false,
        error: 'google_calendar_not_connected',
        lastFetch: new Date()
      });
      
      return [];
    }
  }, [isAuthenticated, token, refreshToken]);

  const saveCalendarPreferences = async (calendars) => {
    try {
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch('/api/google/calendars/preferences', {
        method: 'POST',
        headers,
        body: JSON.stringify({ calendars })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save calendar preferences');
      }

      // Update local state with saved preferences
      setCalendarData(prev => ({
        ...prev,
        calendars: prev.calendars.map(cal => {
          const savedCal = calendars.find(c => c.calendar_id === cal.id);
          return savedCal ? {
            ...cal,
            enabled: savedCal.is_enabled,
            color: savedCal.color,
            displayOrder: savedCal.display_order || 0
          } : cal;
        })
      }));

      return data;
    } catch (error) {
      console.error('Error saving calendar preferences:', error);
      throw error;
    }
  };

  const toggleCalendar = (calendarId, tokenId, enabled) => {
    // Update local state only - don't save automatically
    setCalendarData(prev => ({
      ...prev,
      calendars: prev.calendars.map(cal =>
        (cal.id === calendarId && cal.tokenId === tokenId) ? { ...cal, enabled } : cal
      )
    }));
  };

  const updateCalendarColor = (calendarId, tokenId, color) => {
    // Update local state only - don't save automatically
    setCalendarData(prev => ({
      ...prev,
      calendars: prev.calendars.map(cal =>
        (cal.id === calendarId && cal.tokenId === tokenId) ? { ...cal, color } : cal
      )
    }));
  };

  const reorderCalendars = async (reorderedCalendars) => {
    try {
      // Update local state optimistically
      setCalendarData(prev => ({
        ...prev,
        calendars: reorderedCalendars
      }));

      // Prepare data for API
      const calendarsToSave = reorderedCalendars.map((cal, index) => ({
        calendar_id: cal.id,
        calendar_name: cal.name,
        color: cal.color,
        is_enabled: cal.enabled,
        display_order: index,
        token_id: cal.tokenId
      }));

      await saveCalendarPreferences(calendarsToSave);
    } catch (error) {
      // Revert optimistic update on error
      await fetchCalendars();
      throw error;
    }
  };

  // REMOVED: Automatic calendar list fetching on auth
  // Calendar list should only be fetched when user opens calendar configuration
  // For normal app usage, use useEnabledCalendars hook instead

  const saveAllCalendarPreferences = async () => {
    const calendarsToSave = calendarData.calendars.map((cal, index) => ({
      calendar_id: cal.id,
      calendar_name: cal.name,
      color: cal.color,
      is_enabled: cal.enabled,
      display_order: index,
      token_id: cal.tokenId
    }));

    return await saveCalendarPreferences(calendarsToSave);
  };

  return {
    ...calendarData,
    fetchCalendars,
    saveCalendarPreferences,
    saveAllCalendarPreferences,
    toggleCalendar,
    updateCalendarColor,
    reorderCalendars,
    
    // Helper getters
    enabledCalendars: calendarData.calendars.filter(cal => cal.enabled),
    disabledCalendars: calendarData.calendars.filter(cal => !cal.enabled),
    hasEnabledCalendars: calendarData.calendars.some(cal => cal.enabled)
  };
};